import { detach, isNullOrUndefined } from '@syncfusion/ej2-base';
import { getContextMenuItems } from '../../common/utils/data';
import { events } from '../../common/constant';
import { sanitizeContextMenuItems } from '../../common/utils/transform';
import * as constants from '../../common/constant';
/**
 * `ContextMenuModule` is used to handle the context menu actions in the BlockEditor.
 *
 * @hidden
 */
export class ContextMenuModule {
    constructor(editor) {
        this.editor = editor;
        this.init();
        this.addEventListeners();
    }
    addEventListeners() {
        this.editor.on(events.moduleChanged, this.onPropertyChanged, this);
        this.editor.blockManager.observer.on('enableDisableContextMenuItems', this.enableMenuItems, this);
        this.editor.on(events.destroy, this.destroy, this);
    }
    removeEventListeners() {
        this.editor.off(events.moduleChanged, this.onPropertyChanged);
        this.editor.blockManager.observer.off('enableDisableContextMenuItems', this.enableMenuItems);
        this.editor.off(events.destroy, this.destroy);
    }
    init() {
        this.menuElement = this.editor.createElement('ul', {
            id: (this.editor.element.id + constants.BLOCKEDITOR_CONTEXTMENU_ID)
        });
        document.body.appendChild(this.menuElement);
        const itemTemplate = '${if(!separator)}' +
            '<div class="e-ctmenu-item-template">' +
            '<div class="e-ctmenu-content">' +
            '<span class="e-ctmenu-icon ${iconCss}"></span>' +
            '<span class="e-ctmenu-text">${text}</span>' +
            '</div>' +
            '${if(shortcut)}' +
            '<div class="e-ctmenu-shortcut">${shortcut}</div>' +
            '${/if}' +
            '</div>' +
            '${/if}';
        this.contextMenuObj = this.editor.menubarRenderer.renderContextMenu({
            target: '#' + this.editor.element.id,
            cssClass: constants.BLOCKEDITOR_CONTEXTMENU_CLS,
            element: this.menuElement,
            items: this.getMenuItems(),
            showItemOnClick: this.editor.contextMenuSettings.showItemOnClick,
            itemTemplate: itemTemplate,
            fields: { text: 'text', iconCss: 'iconCss', itemId: 'id' },
            select: this.handleContextMenuSelection.bind(this),
            beforeOpen: this.handleContextMenuBeforeOpen.bind(this),
            beforeClose: this.handleContextMenuBeforeClose.bind(this),
            open: this.handleContextMenuOpen.bind(this),
            close: this.handleContextMenuClose.bind(this)
        });
        this.editor.blockManager.observer.notify('contextMenuCreated');
    }
    getMenuItems() {
        const menuItems = this.editor.contextMenuSettings.items.length > 0
            ? sanitizeContextMenuItems(this.editor.contextMenuSettings.items)
            : getContextMenuItems();
        if (this.editor.contextMenuSettings.items.length <= 0) {
            const prevOnChange = this.editor.isProtectedOnChange;
            this.editor.isProtectedOnChange = true;
            this.editor.contextMenuSettings.items = menuItems;
            this.editor.isProtectedOnChange = prevOnChange;
        }
        return menuItems;
    }
    handleContextMenuBeforeOpen(args) {
        const eventArgs = {
            event: args.event,
            items: this.editor.contextMenuSettings.items,
            parentItem: args.parentItem,
            cancel: !this.editor.contextMenuSettings.enable
        };
        if (this.editor.contextMenuSettings.beforeOpen) {
            this.editor.contextMenuSettings.beforeOpen.call(this, eventArgs);
        }
        args.cancel = eventArgs.cancel;
        if (this.editor.readOnly) {
            args.cancel = true;
        }
        if (!args.cancel) {
            this.editor.blockManager.observer.notify('contextMenuBeforeOpen', args);
        }
    }
    handleContextMenuBeforeClose(args) {
        const eventArgs = {
            event: args.event,
            items: this.editor.contextMenuSettings.items,
            parentItem: args.parentItem,
            cancel: false
        };
        if (this.editor.contextMenuSettings.beforeClose) {
            this.editor.contextMenuSettings.beforeClose.call(this, eventArgs);
        }
        args.cancel = eventArgs.cancel;
    }
    handleContextMenuOpen(args) {
        this.editor.blockManager.observer.notify('updateContextMenuState', { value: { isOpen: true } });
    }
    handleContextMenuClose(args) {
        this.editor.blockManager.observer.notify('updateContextMenuState', { value: { isOpen: false } });
    }
    handleContextMenuSelection(args) {
        const clickEventArgs = {
            item: args.item,
            event: args.event,
            cancel: false
        };
        if (this.editor.contextMenuSettings.itemSelect) {
            this.editor.contextMenuSettings.itemSelect.call(this, clickEventArgs);
        }
        if (!clickEventArgs.cancel) {
            this.editor.blockManager.observer.notify('contextMenuSelection', args);
        }
    }
    enableMenuItems(menuState) {
        if (this.contextMenuObj) {
            const itemIds = Object.keys(menuState);
            itemIds.forEach((item) => {
                this.contextMenuObj.enableItems([item], menuState[item], true);
            });
        }
    }
    /**
     * For internal use only - Get the module name.
     *
     * @returns {void}
     * @hidden
     */
    getModuleName() {
        return 'contextMenuSettings';
    }
    /**
     * Destroys the ContextMenu module.
     *
     * @returns {void}
     */
    destroy() {
        if (this.contextMenuObj) {
            this.contextMenuObj.destroy();
            this.contextMenuObj = null;
            detach(this.menuElement);
            this.menuElement = null;
        }
        this.removeEventListeners();
    }
    /**
     * Called internally if any of the property value changed.
     *
     * @param {BlockEditorModel} e - specifies the element.
     * @returns {void}
     * @hidden
     */
    onPropertyChanged(e) {
        if (e.module !== this.getModuleName()) {
            return;
        }
        const newProp = e.newProp.contextMenuSettings;
        if (!isNullOrUndefined(newProp)) {
            for (const prop of Object.keys(newProp)) {
                switch (prop) {
                    case 'showItemOnClick':
                        this.contextMenuObj.showItemOnClick = this.editor.blockManager.contextMenuSettings.showItemOnClick =
                            newProp.showItemOnClick;
                        break;
                    case 'items':
                        this.contextMenuObj.items = this.editor.blockManager.contextMenuSettings.items =
                            sanitizeContextMenuItems(newProp.items);
                        break;
                    case 'itemTemplate':
                        this.contextMenuObj.itemTemplate = newProp.itemTemplate;
                        break;
                }
            }
        }
    }
}
